"use client";

import { forwardRef, ReactNode, RefObject, useEffect, useRef } from "react";
import { HiddenSelect, useButton, useSelect } from "react-aria";
import { Item, useSelectState } from "react-stately";

import { csx } from "../../utils";
import ListBox from "../ListBox";
import Popover from "../Popover";
import ActionMenu, { ActionMenuAction } from "../ActionMenu";
import { BREAKPOINTS } from "../../constants";
import { useBreakpoint } from "../../hooks/useBreakpoint";

import "./Select.css";

interface SelectButtonProps {
  disabled?: boolean;
  isOpen?: boolean;
  children?: ReactNode;
}

export const SelectButton = forwardRef<HTMLButtonElement, SelectButtonProps>(
  (props, ref: RefObject<HTMLButtonElement>) => {
    let { buttonProps } = useButton(
      {
        ...props,
        isDisabled: props.disabled,
      },
      ref
    );

    return (
      <button
        {...buttonProps}
        ref={ref}
        className={csx(
          "proton-Select__trigger",
          props.disabled && "proton-Select__trigger--disabled",
          !props.isOpen && "proton-Select__trigger--closed",
          props.isOpen && "proton-Select__trigger--opened"
        )}
      >
        {props.children}
      </button>
    );
  }
);

interface SelectItem {
  key: string;
  label: string;
  to?: string;
  onAction?: (key: string) => void;
}

export interface SelectProps {
  /** The name of the select field */
  name: string;

  /** Label to display above the select
   * @external https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label#text
   */
  label?: string;

  /** Whether the select is disabled */
  isDisabled?: boolean;

  /** Array of keys that should be disabled
   * @example ["Thing 1", "Thing 2"]
   */
  disabledKeys?: string[];

  /** Currently selected key */
  selectedKey?: string;

  /** Callback fired when selection changes */
  onSelectionChange?: (key: string) => void;

  /** Callback fired when the menu opens */
  onOpen?: () => void;

  /** Callback fired when the menu closes */
  onClose?: () => void;

  /** Array of items to display
   * @example [{ key: "thing-1", label: "Thing 1" }, { key: "thing-2", label: "Thing 2" }]
   */
  items: SelectItem[];

  /** Test ID for the select */
  "data-testid"?: string;
}

/**
 * A dropdown select menu that opens a popover on desktop and an action menu on mobile.
 * Supports controlled and uncontrolled modes.
 */
export function SelectMenu({
  label,
  name,
  isDisabled,
  disabledKeys,
  selectedKey,
  onSelectionChange,
  onOpen,
  onClose,
  items,
  "data-testid": testId,
}: SelectProps) {
  const selectProps = {
    label,
    isDisabled,
    disabledKeys,
    selectedKey,
    onSelectionChange,
    onOpenChange: (isOpen: boolean) => {
      if (isOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    "data-testid": testId,
    children: items.map((item) => (
      <Item key={item.key} textValue={item.label}>
        {item.label}
      </Item>
    )),
  };

  const state = useSelectState(selectProps);
  const isMobile = useBreakpoint(BREAKPOINTS.MEDIUM);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { labelProps, triggerProps, valueProps, menuProps } = useSelect(
    { ...selectProps, label: label || name },
    state,
    triggerRef
  );

  useEffect(() => {
    if (
      state.isOpen &&
      popoverRef.current &&
      triggerRef.current &&
      !popoverRef.current.style.minWidth
    ) {
      // Adjust the popover width to be at least the width of the trigger
      popoverRef.current.style.minWidth = `${triggerRef.current.offsetWidth}px`;
    }
  }, [state.isOpen]);

  const actionItems: ActionMenuAction[] = items.map((item) => ({
    key: item.key,
    label: item.label,
    to: item.to,
    onAction: (key: string) => {
      onSelectionChange?.(key);
      item.onAction?.(key);
      state.close();
    },
  }));

  return (
    <div
      className="proton-Select"
      aria-label={label || name}
      aria-disabled={isDisabled}
    >
      {label ? (
        <div
          {...labelProps}
          className="proton-Select__label"
          data-testid="select-label"
        >
          {label}
        </div>
      ) : (
        // If no label is provided, render an invisible label for a11y
        <div {...labelProps} />
      )}

      <HiddenSelect
        isDisabled={isDisabled}
        state={state}
        triggerRef={triggerRef}
        label={label || name}
        name={name}
        data-testid="hidden-select-container"
      />

      <SelectButton
        {...triggerProps}
        isOpen={state.isOpen}
        ref={triggerRef}
        disabled={isDisabled}
        data-testid={testId}
      >
        <div {...valueProps} className="proton-Select__value">
          {state.selectedItem
            ? state.selectedItem.rendered
            : "Select an option"}
        </div>
        <div
          className={csx(
            "proton-Select__trigger_icon",
            state.isOpen && "proton-Select__trigger_icon--flipped"
          )}
          aria-hidden="true"
        >
          <svg id="caret-down" viewBox="0 0 512 512">
            <title>{state.isOpen ? "caret down" : "caret up"}</title>
            <path
              fill="currentColor"
              d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7L86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"
            />
          </svg>
        </div>
      </SelectButton>

      {state.isOpen && (
        <>
          {!isMobile ? (
            <div className="proton-Select__popover">
              <Popover
                state={state}
                triggerRef={triggerRef}
                popoverRef={popoverRef}
                placement="bottom start"
                data-testid="select-popover"
              >
                <ListBox {...menuProps} state={state} />
              </Popover>
            </div>
          ) : (
            <ActionMenu
              isOpen={state.isOpen}
              onClose={() => state.close()}
              actions={actionItems}
              selectedKeys={selectedKey ? [selectedKey] : undefined}
            />
          )}
        </>
      )}
    </div>
  );
}

const Select = {
  Menu: SelectMenu,
};

export default Select;
